import { useEffect } from 'react';
import axios from 'axios';
import apiClient from '@assetron/common/api/apiClient';
import {
  ECONNABORTED,
  DUMMY_RECAPTCHA_TOKEN,
  SUPPRESS_SERVER_ERROR_TOAST_HEADER,
} from '@assetron/common/constants/constant';
import { ResponseStatusValuesNumbers } from '@assetron/common/types/responseStatus';
import { useLocalization } from '@assetron/common/contexts/localization/LocalizationContext';
import { getAppCheckToken } from '@assetron/common/contexts/AuthWebContext';
import { notifyToaster } from '../components/Toaster/ToasterService';

/** Interface for errors that can be marked as handled */
interface ErrorWithHandled extends Error {
  handled?: boolean;
}

/**
 * Helper function to wrap error objects consistently and mark as handled
 * @param error - The error to wrap
 * @param handled - Whether to mark the error as handled
 * @returns A properly wrapped Error object
 */
const wrapError = (error: unknown, handled = false): ErrorWithHandled => {
  let wrappedError: ErrorWithHandled;

  if (error instanceof Error) {
    wrappedError = error as ErrorWithHandled;
  } else {
    let errorMessage: string;

    if (typeof error === 'object' && error !== null && 'message' in error) {
      errorMessage = String(error.message);
    } else {
      errorMessage = String(error);
    }

    wrappedError = new Error(errorMessage);
  }

  if (handled) {
    wrappedError.handled = true;
  }

  return wrappedError;
};

export function useRecaptchaInterceptor(
  executeRecaptcha: (action: string) => Promise<string>,
) {
  const { t } = useLocalization();
  useEffect(() => {
    const requestInterceptor = apiClient.interceptors.request.use(
      async (config) => {
        const newConfig = { ...config };
        newConfig.headers['Recaptcha-Token'] = DUMMY_RECAPTCHA_TOKEN;
        // Add App Check token to all API calls
        try {
          const appCheckToken = await getAppCheckToken();
          if (appCheckToken) {
            newConfig.headers['X-Firebase-AppCheck'] = appCheckToken;
          }
        } catch {
          // If we can't get the token, continue with the request anyway
        }
        return newConfig;
      },
    );

    const authInterceptor = axios.interceptors.request.use(async (config) => {
      const newConfig = { ...config };
      newConfig.headers['Recaptcha-Token'] = DUMMY_RECAPTCHA_TOKEN;
      // Add App Check token to all API calls
      try {
        const appCheckToken = await getAppCheckToken();
        if (appCheckToken) {
          newConfig.headers['X-Firebase-AppCheck'] = appCheckToken;
        }
      } catch {
        // If we can't get the token, continue with the request anyway
      }
      return newConfig;
    });

    const responseInterceptor = apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.code === ECONNABORTED) {
          notifyToaster({
            type: 'error',
            message: t('commonApiErrors.timeoutError'),
          });
          return Promise.reject(wrapError(error, true));
        }
        if (!error.response) {
          notifyToaster({
            type: 'error',
            message: t('commonApiErrors.networkError'),
          });
          return Promise.reject(wrapError(error, true));
        }
        if (
          error.response.status >=
          ResponseStatusValuesNumbers.InternalServerError
        ) {
          const headers = error.config?.headers as
            | (Record<string, unknown> & { get?: (key: string) => unknown })
            | undefined;
          const suppressValueRaw =
            typeof headers?.get === 'function'
              ? headers.get(SUPPRESS_SERVER_ERROR_TOAST_HEADER)
              : headers?.[SUPPRESS_SERVER_ERROR_TOAST_HEADER];
          const suppressValueNormalized = Array.isArray(suppressValueRaw)
            ? suppressValueRaw[0]
            : suppressValueRaw;
          let suppressValue: string | undefined;
          if (typeof suppressValueNormalized === 'string') {
            suppressValue = suppressValueNormalized;
          } else if (suppressValueNormalized == null) {
            suppressValue = undefined;
          } else {
            suppressValue = String(suppressValueNormalized);
          }
          const suppressToaster = Boolean(
            suppressValue && suppressValue.toLowerCase() === 'true',
          );
          if (suppressToaster) {
            return Promise.reject(wrapError(error, true));
          }
          notifyToaster({
            type: 'error',
            message: t('commonApiErrors.serverError'),
          });
          return Promise.reject(wrapError(error, true));
        }
        return Promise.reject(wrapError(error));
      },
    );

    return () => {
      apiClient.interceptors.request.eject(requestInterceptor);
      apiClient.interceptors.response.eject(responseInterceptor);
      axios.interceptors.request.eject(authInterceptor);
    };
  }, [executeRecaptcha, t]);
}
